import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COORDINATION_AGENDA } from "@/lib/basProcess/constants";
import { CalendarDays, CalendarPlus, Plus, Send, Trash2, FileDown, AlertTriangle, Loader2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  buildCoordinationPrefill,
  readDecisions,
  type MeetingDecision,
} from "@/lib/samordning/prefill";
import { generateCoordinationMeetingWordDocument } from "@/lib/documentGenerator/samordningWordGenerator";
import { buildIcs, downloadIcs, isValidEmail } from "@/lib/samordning/ics";


interface Props {
  projectId: string;
}

interface Attendee {
  id: string;
  name: string;
  role: string;
  email: string;
}

interface Meeting {
  id: string;
  meeting_date: string;
  participants: string | null;
  content_json: Record<string, any>;
  status: string;
  stage_id: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  is_online: boolean;
  meeting_url: string | null;
  attendees: Attendee[];
  ics_uid: string | null;
  ics_sequence: number;
  invite_sent_at: string | null;
  invite_sent_to: string[];
}

const MEETING_COLUMNS =
  "id, meeting_date, participants, content_json, status, stage_id, start_time, end_time, location, is_online, meeting_url, attendees, ics_uid, ics_sequence, invite_sent_at, invite_sent_to";

export const CoordinationMeetings: React.FC<Props> = ({ projectId }) => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [stages, setStages] = useState<{ id: string; name: string }[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [gaps, setGaps] = useState<string[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("coordination_meetings")
      .select(MEETING_COLUMNS)
      .eq("project_id", projectId)
      .order("meeting_date", { ascending: false });
    if (error) toast.error("Kunde inte läsa mötena");
    else
      setMeetings(
        (data || []).map((m: any) => ({
          ...m,
          content_json: m.content_json || {},
          attendees: Array.isArray(m.attendees) ? (m.attendees as Attendee[]) : [],
          invite_sent_to: Array.isArray(m.invite_sent_to) ? (m.invite_sent_to as string[]) : [],
        }))
      );

    setLoading(false);
  };

  const loadStages = async () => {
    const { data } = await supabase
      .from("project_stages")
      .select("id, name")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    setStages(data || []);
  };

  const loadProject = async () => {
    const { data } = await supabase.from("projects").select("name").eq("id", projectId).maybeSingle();
    setProjectName(data?.name ?? "");
  };

  const loadGaps = async () => {
    try {
      const prefill = await buildCoordinationPrefill(projectId);
      setGaps(prefill.gaps);
    } catch {
      setGaps([]);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    loadStages();
    loadProject();
    loadGaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    setCreating(true);
    try {
      const lastDate = meetings[0]?.meeting_date ?? null;
      const prefill = await buildCoordinationPrefill(projectId, lastDate);
      const { data, error } = await supabase
        .from("coordination_meetings")
        .insert({
          project_id: projectId,
          content_json: { ...prefill.content, decisions: prefill.decisions } as any,
        })
        .select("id")
        .single();
      if (error) throw error;
      setGaps(prefill.gaps);
      await load();
      setOpenId(data.id);
      toast.success("Möte skapat – agendan är förifylld från projektet");
    } catch {
      toast.error("Kunde inte skapa mötet");
    } finally {
      setCreating(false);
    }
  };

  const update = async (id: string, patch: Partial<Meeting>) => {
    setMeetings((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    const { error } = await supabase.from("coordination_meetings").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const updateField = (m: Meeting, key: string, value: string) =>
    update(m.id, { content_json: { ...m.content_json, [key]: value } });

  const updateDecisions = (m: Meeting, decisions: MeetingDecision[]) =>
    update(m.id, { content_json: { ...m.content_json, decisions } });

  const remove = async (id: string) => {
    const { error } = await supabase.from("coordination_meetings").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort mötet");
    setMeetings((prev) => prev.filter((m) => m.id !== id));
  };

  const refillAgenda = async (m: Meeting) => {
    const previous = meetings.find(
      (other) => other.id !== m.id && other.meeting_date < m.meeting_date
    );
    const prefill = await buildCoordinationPrefill(projectId, previous?.meeting_date ?? null);
    const merged: Record<string, any> = { ...m.content_json };
    for (const [k, v] of Object.entries(prefill.content)) {
      if (!String(merged[k] ?? "").trim()) merged[k] = v;
    }
    const existing = readDecisions(m.content_json);
    const extra = prefill.decisions.filter((d) => !existing.some((e) => e.text === d.text));
    merged.decisions = [...existing, ...extra];
    await update(m.id, { content_json: merged });
    setGaps(prefill.gaps);
    toast.success("Agendan uppdaterad med aktuellt underlag");
  };

  const exportMeeting = async (m: Meeting) => {
    try {
      await generateCoordinationMeetingWordDocument({
        meetingDate: m.meeting_date,
        startTime: m.start_time,
        endTime: m.end_time,
        location: m.is_online ? m.meeting_url || "Digitalt möte" : m.location,
        participants: participantsText(m),
        stageName: m.stage_id ? stages.find((s) => s.id === m.stage_id)?.name ?? null : null,
        agenda: COORDINATION_AGENDA.map((a) => ({
          label: a.label,
          text: String(m.content_json?.[a.key] ?? ""),
        })),
        decisions: readDecisions(m.content_json),
        projectName,
      });
      toast.success("Protokollet exporterat");
    } catch {
      toast.error("Kunde inte exportera protokollet");
    }
  };

  const participantsText = (m: Meeting) => {
    const fromList = (m.attendees || [])
      .map((a) => [a.name, a.role].filter(Boolean).join(", "))
      .filter(Boolean)
      .join("; ");
    return fromList || m.participants || null;
  };

  const agendaText = (m: Meeting) =>
    COORDINATION_AGENDA.map((a) => {
      const text = String(m.content_json?.[a.key] ?? "").trim();
      return text ? `${a.label}: ${text}` : `${a.label}`;
    }).join("\n");

  const updateAttendees = (m: Meeting, attendees: Attendee[]) =>
    update(m.id, { attendees });

  const calendarPayload = (m: Meeting, method: "REQUEST" | "CANCEL" = "REQUEST") => ({
    uid: m.ics_uid || `${m.id}@byggarbetsmiljokontrollen.se`,
    sequence: m.ics_sequence ?? 0,
    date: m.meeting_date,
    startTime: m.start_time,
    endTime: m.end_time,
    location: m.location,
    isOnline: m.is_online,
    meetingUrl: m.meeting_url,
    summary: `Samordningsmöte – ${projectName || "Projekt"}`,
    description: agendaText(m),
    attendees: (m.attendees || []).filter((a) => isValidEmail(a.email || "")),
    method,
  });

  const downloadMeetingIcs = (m: Meeting) => {
    downloadIcs(`Samordningsmote_${m.meeting_date}`, buildIcs(calendarPayload(m)));
    toast.success("Kalenderfilen är nedladdad");
  };

  const sendInvite = async (m: Meeting, mode: "invite" | "cancel") => {
    const valid = (m.attendees || []).filter((a) => isValidEmail(a.email || ""));
    if (valid.length === 0) {
      toast.error("Lägg till minst en deltagare med e-postadress");
      return;
    }
    setSendingId(m.id);
    try {
      const { data, error } = await supabase.functions.invoke("send-meeting-invite", {
        body: { meeting_id: m.id, mode, agenda_text: agendaText(m) },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(
        mode === "cancel"
          ? "Avbokning skickad till deltagarna"
          : `Kallelse skickad till ${valid.length} deltagare`
      );
      await load();
    } catch (err: any) {
      toast.error(err?.message || "Kunde inte skicka kallelsen");
    } finally {
      setSendingId(null);
    }
  };


  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="29"
        title="Samordningsmöten"
        description="Protokollför samordningsmötena. Agendan fylls automatiskt med öppna risker, ändringar, tillbud och beslut som inte är stängda."
        icon={CalendarDays}
        projectId={projectId}
        actions={
          <Button size="sm" onClick={create} disabled={creating}>
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Nytt möte
          </Button>
        }
      />

      {gaps.length > 0 && (
        <Card className="border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/10">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Saknat underlag inför nästa möte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {gaps.map((g) => (
              <div key={g}>• {g}</div>
            ))}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : meetings.length === 0 ? (
        <p className="text-sm text-muted-foreground">Inga möten registrerade ännu.</p>
      ) : (
        meetings.map((m) => {
          const isOpen = openId === m.id;
          const filled = COORDINATION_AGENDA.filter((a) =>
            String(m.content_json?.[a.key] ?? "").trim()
          ).length;
          const decisions = readDecisions(m.content_json);
          const openDecisions = decisions.filter((d) => !d.done).length;

          return (
            <Card key={m.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button className="text-left" onClick={() => setOpenId(isOpen ? null : m.id)}>
                    <CardTitle className="text-base">Möte {m.meeting_date}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {m.participants || "Inga deltagare angivna"}
                    </p>
                  </button>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {filled}/{COORDINATION_AGENDA.length} punkter
                    </Badge>
                    {openDecisions > 0 && (
                      <Badge variant="secondary">{openDecisions} öppna beslut</Badge>
                    )}
                    <Button variant="outline" size="sm" onClick={() => exportMeeting(m)}>
                      <FileDown className="mr-1 h-4 w-4" /> Protokoll
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(m.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {isOpen && (
                <CardContent className="space-y-4">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Datum</label>
                      <Input
                        type="date"
                        defaultValue={m.meeting_date}
                        onBlur={(e) => update(m.id, { meeting_date: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Från</label>
                        <Input
                          type="time"
                          defaultValue={m.start_time?.slice(0, 5) ?? ""}
                          onBlur={(e) => update(m.id, { start_time: e.target.value || null })}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Till</label>
                        <Input
                          type="time"
                          defaultValue={m.end_time?.slice(0, 5) ?? ""}
                          onBlur={(e) => update(m.id, { end_time: e.target.value || null })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Plats</label>
                      <Input
                        placeholder="T.ex. Byggbod, Storgatan 1"
                        defaultValue={m.location ?? ""}
                        onBlur={(e) => update(m.id, { location: e.target.value || null })}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        Länk till digitalt möte (valfritt)
                      </label>
                      <Input
                        placeholder="https://teams.microsoft.com/..."
                        defaultValue={m.meeting_url ?? ""}
                        onBlur={(e) =>
                          update(m.id, {
                            meeting_url: e.target.value || null,
                            is_online: !!e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2 rounded-md border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium">Deltagare och kallelse</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateAttendees(m, [
                            ...(m.attendees || []),
                            { id: crypto.randomUUID(), name: "", role: "", email: "" },
                          ])
                        }
                      >
                        <Plus className="mr-1 h-4 w-4" /> Lägg till deltagare
                      </Button>
                    </div>

                    {(m.attendees || []).length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Lägg till deltagare med e-post för att kunna skicka kallelse med kalenderfil.
                      </p>
                    )}

                    {(m.attendees || []).map((a, i) => (
                      <div key={a.id} className="grid gap-2 sm:grid-cols-[1fr_140px_1fr_auto]">
                        <Input
                          placeholder="Namn"
                          defaultValue={a.name}
                          onBlur={(e) => {
                            const next = [...(m.attendees || [])];
                            next[i] = { ...a, name: e.target.value };
                            updateAttendees(m, next);
                          }}
                        />
                        <Input
                          placeholder="Roll"
                          defaultValue={a.role}
                          onBlur={(e) => {
                            const next = [...(m.attendees || [])];
                            next[i] = { ...a, role: e.target.value };
                            updateAttendees(m, next);
                          }}
                        />
                        <Input
                          type="email"
                          placeholder="E-post"
                          defaultValue={a.email}
                          onBlur={(e) => {
                            const next = [...(m.attendees || [])];
                            next[i] = { ...a, email: e.target.value.trim() };
                            updateAttendees(m, next);
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            updateAttendees(m, (m.attendees || []).filter((x) => x.id !== a.id))
                          }
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    ))}

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={() => downloadMeetingIcs(m)}>
                        <CalendarPlus className="mr-1 h-4 w-4" /> Lägg till i min kalender
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => sendInvite(m, "invite")}
                        disabled={sendingId === m.id}
                      >
                        {sendingId === m.id ? (
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-1 h-4 w-4" />
                        )}
                        {m.invite_sent_at ? "Skicka uppdaterad kallelse" : "Skicka kallelse"}
                      </Button>
                      {m.invite_sent_at && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => sendInvite(m, "cancel")}
                          disabled={sendingId === m.id}
                        >
                          Avboka mötet
                        </Button>
                      )}
                    </div>

                    {m.invite_sent_at && (
                      <p className="text-xs text-muted-foreground">
                        Kallelse skickad {new Date(m.invite_sent_at).toLocaleString("sv-SE")} till{" "}
                        {(m.invite_sent_to || []).join(", ") || "deltagarna"}.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      Övriga deltagare (fritext)
                    </label>
                    <Input
                      placeholder="Deltagare (namn, roll)"
                      defaultValue={m.participants ?? ""}
                      onBlur={(e) => update(m.id, { participants: e.target.value })}
                    />
                  </div>

                  {stages.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Etapp</label>
                      <Select
                        value={m.stage_id ?? "none"}
                        onValueChange={(v) => update(m.id, { stage_id: v === "none" ? null : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Hela projektet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Hela projektet</SelectItem>
                          {stages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={() => refillAgenda(m)}>
                      Hämta aktuellt underlag
                    </Button>
                  </div>

                  {COORDINATION_AGENDA.map((a) => (
                    <div key={a.key} className="space-y-1">
                      <label className="text-sm font-medium">{a.label}</label>
                      {a.hint && <p className="text-xs text-muted-foreground">{a.hint}</p>}
                      <Textarea
                        rows={3}
                        defaultValue={String(m.content_json?.[a.key] ?? "")}
                        onBlur={(e) => updateField(m, a.key, e.target.value)}
                      />
                    </div>
                  ))}

                  <div className="space-y-2 rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Beslut och åtgärder</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateDecisions(m, [
                            ...decisions,
                            { id: crypto.randomUUID(), text: "", responsible: "", dueDate: "" },
                          ])
                        }
                      >
                        <Plus className="mr-1 h-4 w-4" /> Lägg till
                      </Button>
                    </div>
                    {decisions.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Inga beslut registrerade. Öppna punkter följer automatiskt med till nästa möte.
                      </p>
                    )}
                    {decisions.map((d, i) => (
                      <div key={d.id} className="grid gap-2 sm:grid-cols-[1fr_150px_140px_auto] sm:items-center">
                        <Input
                          placeholder="Beslut/åtgärd"
                          defaultValue={d.text}
                          onBlur={(e) => {
                            const next = [...decisions];
                            next[i] = { ...d, text: e.target.value };
                            updateDecisions(m, next);
                          }}
                        />
                        <Input
                          placeholder="Ansvarig"
                          defaultValue={d.responsible ?? ""}
                          onBlur={(e) => {
                            const next = [...decisions];
                            next[i] = { ...d, responsible: e.target.value };
                            updateDecisions(m, next);
                          }}
                        />
                        <Input
                          type="date"
                          defaultValue={d.dueDate ?? ""}
                          onBlur={(e) => {
                            const next = [...decisions];
                            next[i] = { ...d, dueDate: e.target.value };
                            updateDecisions(m, next);
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-1 text-xs">
                            <Checkbox
                              checked={Boolean(d.done)}
                              onCheckedChange={(v) => {
                                const next = [...decisions];
                                next[i] = { ...d, done: Boolean(v) };
                                updateDecisions(m, next);
                              }}
                            />
                            Stängd
                          </label>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateDecisions(m, decisions.filter((x) => x.id !== d.id))}
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </div>
                        {d.fromMeeting && (
                          <p className="text-xs text-muted-foreground sm:col-span-4">
                            Från möte {d.fromMeeting}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
};

export default CoordinationMeetings;
